import React from 'react';
import { ButtonV2, Flex, FormControl, InputV2, TextV2 } from '@withjoy/joykit';
import { useOtpStepController } from './OtpStep.controller';
import { useTranslation } from '@shared/core';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { body4ToSubHeadingOverride, display4ToHeadingOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { StyledContent, StyledHeaderStack, StyledTitleStack, StyledFormStack, StyledInputsStack, styles } from '../../UnlockDialog/UnlockForm/UnlockForm.styles';
import { useDeferredInputFocus } from '../useDeferredInputFocus';

type OtpStepProps = Readonly<{
  eventId: string;
  email: string;
  onSuccess: (firstName: string | null | undefined) => void;
  onSkip: () => void;
}>;

export const OtpStep: React.FC<OtpStepProps> = ({ eventId, email, onSuccess, onSkip }) => {
  const { formik, loading, resending, handleResend } = useOtpStepController({ eventId, email, onSuccess });
  const { tq } = useTranslation('guestUnlock');
  const inputRef = useDeferredInputFocus<HTMLInputElement>();

  return (
    <StyledContent __css={styles.content}>
      <StyledHeaderStack>
        <StyledTitleStack>
          <GuestSiteTypographyOverride override={display4ToHeadingOverride}>
            <TextV2 typographyVariant="hed5">{tq`Check your email`}</TextV2>
          </GuestSiteTypographyOverride>
          <GuestSiteTypographyOverride override={body4ToSubHeadingOverride}>
            <TextV2
              typographyVariant="body2"
              color="mono10"
            >
              {tq`Enter the 6-digit code we sent to ${email}.`}
            </TextV2>
          </GuestSiteTypographyOverride>
        </StyledTitleStack>
      </StyledHeaderStack>
      <StyledFormStack
        spacing={6}
        __css={styles.formStack}
      >
        <form onSubmit={formik.handleSubmit}>
          <StyledInputsStack>
            <FormControl label={tq`Verification code`}>
              <InputV2
                ref={inputRef}
                placeholder="000000"
                isInvalid={(!!formik.errors.code && formik.touched.code) || undefined}
                isDisabled={loading}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                {...formik.getFieldProps('code')}
              />
            </FormControl>
          </StyledInputsStack>
          <ButtonV2
            shape="rounded"
            width="100%"
            intent="neutral"
            type="submit"
            loading={loading}
          >
            {tq`Verify`}
          </ButtonV2>
          <Flex
            justifyContent="center"
            marginTop={4}
          >
            <ButtonV2
              variant="ghost"
              intent="neutral"
              typographyVariant="button2"
              width="fit-content"
              height="fit-content"
              padding={0}
              borderBottom="1px solid"
              borderBottomColor="mono5"
              borderBottomLeftRadius="0px"
              borderBottomRightRadius="0px"
              onClick={handleResend}
              disabled={resending}
            >
              {tq`Resend code`}
            </ButtonV2>
          </Flex>
          <ButtonV2
            variant="ghost"
            intent="neutral"
            typographyVariant="button2"
            width="100%"
            marginTop={4}
            onClick={onSkip}
          >
            {tq`Continue as guest`}
          </ButtonV2>
        </form>
      </StyledFormStack>
    </StyledContent>
  );
};
